import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SpinnerWheel } from "../../../games";
import { GamingConfiguration, GAME_TYPE } from "../../../../constants/types";
import { PiHandTapFill } from "react-icons/pi";
import GamingFooter from "../../../games/GamingFooter";
import { HiArrowDown } from "react-icons/hi";

interface GamingPreviewProps {
  gamingConfig?: GamingConfiguration;
}

const GamingPreview: React.FC<GamingPreviewProps> = ({ gamingConfig }) => {
  const { t } = useTranslation("clientView");
  const [spinnerSize, setSpinnerSize] = useState(300);

  useEffect(() => {
    const calculateSpinnerSize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 360) {
        setSpinnerSize(220);
      } else if (screenWidth < 400) {
        setSpinnerSize(250);
      } else if (screenWidth < 480) {
        setSpinnerSize(270);
      } else {
        setSpinnerSize(300);
      }
    };

    calculateSpinnerSize();
    window.addEventListener("resize", calculateSpinnerSize);
    return () => window.removeEventListener("resize", calculateSpinnerSize);
  }, []);

  if (!gamingConfig?.enabled || !gamingConfig.segments?.length) {
    return null;
  }

  const segments = gamingConfig.segments;
  const isSpinner = gamingConfig.game_type === GAME_TYPE.SPINNER;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full border border-purple-300 my-5">
        <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
          1
        </span>
        <span className="text-purple-800 text-sm font-medium">
          {t("games.preview.stepFirst")}
        </span>
      </div>

      <div className="relative w-full max-w-md mx-auto bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl shadow-lg overflow-hidden">
        <div className="p-6 text-center border-b border-gray-700">
          <h1 className="text-xl font-bold text-white mb-1">
            {t("games.overlay.title")}
          </h1>
          <p className="text-gray-400 text-sm">
            {gamingConfig?.description || ""}
          </p>
        </div>

        <div className="p-6 flex flex-col items-center pointer-events-none">
          {isSpinner ? (
            <SpinnerWheel
              segments={segments}
              onSpinComplete={() => {}}
              winningSegmentId={undefined}
              disabled={true}
              isLoading={false}
              size={spinnerSize}
            />
          ) : (
            <div
              className="rounded-xl overflow-hidden shadow-lg"
              style={{
                width: 260,
                height: 173,
                background:
                  "linear-gradient(145deg, #d4af37 0%, #94771d 25%, #e6c547 50%, #b8960f 75%, #d4af37 100%)",
                boxShadow:
                  "inset 0 2px 4px rgba(255,255,255,0.4), inset 0 -2px 4px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.3)",
              }}
            >
              <div className="w-full h-full flex flex-col items-center justify-center">
                <PiHandTapFill className="w-12 h-12 text-amber-900 mb-2 drop-shadow-lg" />
                <p className="text-lg font-bold text-amber-900 drop-shadow-sm">
                  {t("games.scratch.scratchHere")}
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 w-full max-w-xs">
            {isSpinner && (
              <button
                disabled
                className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-full font-bold text-lg opacity-70 cursor-not-allowed shadow-lg"
              >
                {t("games.spinner.spin")}
              </button>
            )}
            <button
              disabled
              className="w-full px-4 py-2 text-gray-400 text-sm cursor-not-allowed"
            >
              {t("games.overlay.skip")}
            </button>
          </div>
        </div>

        <GamingFooter showFooter={true} variant="dark" />
      </div>

      <div className="flex items-center gap-2 mt-6 px-4 py-2 bg-blue-100 rounded-full border border-blue-300">
        <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
          2
        </span>
        <span className="text-blue-800 text-sm font-medium">
          {t("games.preview.stepThen")}
        </span>
      </div>
      <div className="flex flex-col items-center gap-1 py-2">
        <HiArrowDown className="w-8 h-8 text-blue-600" />
      </div>
    </div>
  );
};

export default GamingPreview;
